import * as React from "react";
import { ReplaceReturnType } from "../../utils";
import { FormFieldExtensionKeys } from "./form-field-extension-keys";

export interface GenericForm {
  [key: string]: any | null | undefined;
}

export interface GenericFormFieldWhichIsDebounced {
  [key: string]: any | null | undefined;
}

export enum ValidationEvent {
  onChange = "onChange",
  onBlur = "onBlur",
  onSubmit = "onSubmit",
  onDebouncedPurgatory = "onDebouncedPurgatory",
  onDependencyEvent = "onDependencyEvent",
  onFocus = "onFocus",
  handleEffect = "handleEffect",
}

export const ValidationEventsWithValues = [ValidationEvent.onChange];

export const ValidationEventsWithDebouncing = [
  ValidationEvent.onFocus,
  ValidationEvent.onChange,
  ValidationEvent.onBlur,
  ValidationEvent.onDependencyEvent,
];

export interface GenericFormValidationContext<
  Form extends GenericForm,
  App extends unknown
> {
  validationEventHistory: ValidationEvent[];
  validationEvent: ValidationEvent;
  name: string;
  prevForm: Form;
  form: Form;
  formReference: React.MutableRefObject<Form | null>;

  /**
   * Any additional context you want to send to the validation functions.
   */
  app: App;
}

export interface GenericFormValidationContext_forBeforeValidate<
  Form extends GenericForm,
  App extends unknown
> extends GenericFormValidationContext<Form, App> {
  mutate: {
    deleteByKey: (
      context: GenericFormValidationContext_forBeforeValidate<Form, App>,
      keyToDelete: any
    ) => GenericFormValidationContext_forBeforeValidate<Form, App>;
  };
}

export interface GenericFormValidationContext_forValidation<
  Form extends GenericForm,
  App extends unknown
> extends GenericFormValidationContext<Form, App> {
  mutate: {
    withPartialForm: (
      context: GenericFormValidationContext_forValidation<Form, App>,
      partialForm: Partial<Form>
    ) => GenericFormValidationContext_forValidation<Form, App>;
  };
}

export interface GenericFormValidation_YieldEvent {
  name: string;
  value?: any;
  validationEvent: ValidationEvent;
}

export interface GenericFormValidationContext_forEffect<
  Form extends GenericForm,
  App extends unknown
> extends GenericFormValidationContext_forValidation<Form, App> {
  yield: {
    validationEvent: (
      context: GenericFormValidationContext_forEffect<Form, App>,
      event: GenericFormValidation_YieldEvent
    ) => GenericFormValidationContext_forEffect<Form, App>;
  };
  yielded: {
    validationEvents: GenericFormValidation_YieldEvent[];
  };
}

export interface GenericFormValidationContext_forDebouncedValidation<
  Form extends GenericForm,
  App extends unknown,
  DebouncedField extends GenericFormFieldWhichIsDebounced
> extends GenericFormValidationContext<Form, App> {
  mutate: {
    withParticularDebouncedValidationResult: (
      context: GenericFormValidationContext_forDebouncedValidation<
        Form,
        App,
        DebouncedField
      >,
      debouncedField: DebouncedField
    ) => GenericFormValidationContext_forDebouncedValidation<
      Form,
      App,
      DebouncedField
    >;
  };
}

export interface GenericFormValidationContext_forProducingDebounceEffectDependencies<
  Form extends GenericForm,
  App extends unknown
> extends Omit<
    GenericFormValidationContext<Form, App>,
    "validationEvent" | "validationEventHistory"
  > {}

export interface GenericFormLogicalFieldPresentation {
  required?: boolean;
}

export interface GenericFormLogicalField<
  Form extends GenericForm,
  App extends unknown,
  DebouncedField extends GenericFormFieldWhichIsDebounced = GenericFormFieldWhichIsDebounced
> {
  readonly presentation?: GenericFormLogicalFieldPresentation;
  readonly dependencies?: string[];
  readonly cleanEvents?: readonly ValidationEvent[];
  readonly validationEvents: readonly ValidationEvent[];
  readonly beforeValidate?: (
    context: GenericFormValidationContext_forBeforeValidate<Form, App>
  ) => void;
  readonly validate: (
    context: GenericFormValidationContext_forValidation<Form, App>
  ) => void;

  readonly debouncedValidate?: (
    context: GenericFormValidationContext_forDebouncedValidation<
      Form,
      App,
      DebouncedField
    >
  ) => Promise<any> | Promise<void> | void;
  readonly debounceDelay?: number;
  readonly debouncedValidationResultIsValid?: (
    context: GenericFormValidationContext<Form, App>
  ) => boolean;
  readonly debouncedValidationResultCanBeStale?: boolean;

  readonly handleDebounceDispatch?: (
    context: GenericFormValidationContext<Form, App>
  ) => Promise<any> | Promise<void> | void;
  readonly handleEffect?: (
    context: GenericFormValidationContext_forEffect<Form, App>
  ) => Promise<any> | Promise<void> | void;
  readonly produceEffectDependencies?: (
    context: GenericFormValidationContext_forProducingDebounceEffectDependencies<
      Form,
      App
    >
  ) => any[];
}

export const genericBeforeValidate_removeDefaultKeyedWarning = <
  Form extends GenericForm,
  App extends unknown
>(
  context: GenericFormValidationContext_forBeforeValidate<Form, App>
): void => {
  context.mutate.deleteByKey(context, `${context.name}Warning`);
};
export const genericBeforeValidate_removeDefaultKeyedError = <
  Form extends GenericForm,
  App extends unknown
>(
  context: GenericFormValidationContext_forBeforeValidate<Form, App>
): void => {
  context.mutate.deleteByKey(context, `${context.name}Error`);
};

export interface GenericTextFieldValidationOptions<
  Form extends GenericForm,
  App extends unknown
> {
  required?: boolean;
  trim?: boolean;
  minLength?: number | undefined;
  maxLength?: number | undefined;
  customLength?: (
    value: any,
    options: GenericTextFieldValidationOptions<Form, App>,
    context: GenericFormValidationContext<Form, App>
  ) => { value: any };
  customWarningValidation?: (
    value: any,
    options: GenericTextFieldValidationOptions<Form, App>,
    context: GenericFormValidationContext<Form, App>
  ) => string | null | undefined | false;
  customErrorValidation?: (
    value: any,
    options: GenericTextFieldValidationOptions<Form, App>,
    context: GenericFormValidationContext<Form, App>
  ) => string | null | undefined | false;
  noticeGenerator?: (
    value: any,
    options: GenericTextFieldValidationOptions<Form, App>,
    context: GenericFormValidationContext<Form, App>
  ) => Partial<Form>;

  requirementMessage?: string;

  guard?: (context: GenericFormValidationContext<Form, App>) => boolean;

  valueMustBeOneOf?: (
    context: GenericFormValidationContext<Form, App>
  ) => any[];
}

export const genericValidate_textField =
  <Form extends GenericForm, App extends unknown>(
    options: GenericTextFieldValidationOptions<Form, App>
  ): GenericFormLogicalField<Form, App>["validate"] =>
  (context) => {
    if (options.guard && !options.guard(context)) {
      return;
    }

    const prevValue = (context.prevForm as { [key: string]: any })[
      context.name
    ];
    const currentValue = (context.form as { [key: string]: any })[context.name];

    let newValue = (context.form as { [key: string]: any })[
      context.name
    ] as string;
    if (options.trim) {
      newValue = newValue?.trim();
    }

    if (options.customLength) {
      const { value: customLengthAdjustedValue } = options.customLength(
        newValue,
        options,
        context
      );
      newValue = customLengthAdjustedValue;
    }
    if (options.maxLength) {
      newValue = newValue?.substring(0, options.maxLength);
    }

    if (options.valueMustBeOneOf) {
      const allowed = options.valueMustBeOneOf(context);
      if (!allowed.includes(newValue)) {
        newValue = prevValue;
      }
    }

    let noticeIsOutdated = false;

    /**
     * Minimum length
     */
    if (!noticeIsOutdated) {
      if (options.minLength) {
        if (newValue?.length < options.minLength) {
          noticeIsOutdated = true;
          const partialUpdate_withError = {
            [`${context.name}Error`]: `Minimum length is ${options.minLength} symbols.`,
          } as Partial<Form>;
          context = context.mutate.withPartialForm(
            context,
            partialUpdate_withError
          );
        }
      }
    }
    /**
     * Required
     */
    if (!noticeIsOutdated) {
      const isEmpty = !newValue || newValue?.length < 1;
      if (options.required && isEmpty) {
        noticeIsOutdated = true;
        const partialUpdate_withError = {
          [`${context.name}Error`]:
            options.requirementMessage || "This field is required.",
        } as Partial<Form>;
        context = context.mutate.withPartialForm(
          context,
          partialUpdate_withError
        );
      }
    }
    /**
     * Custom warning validation
     */
    if (!noticeIsOutdated) {
      let validityWarning = null;
      if (options.customWarningValidation) {
        validityWarning = options.customWarningValidation(
          newValue,
          options,
          context
        );
      }
      if (validityWarning) {
        noticeIsOutdated = true;
        const partialUpdate_withError = {
          [`${context.name}Warning`]: validityWarning,
        } as Partial<Form>;
        context = context.mutate.withPartialForm(
          context,
          partialUpdate_withError
        );
      }
    }
    /**
     * Custom error validation
     */
    if (!noticeIsOutdated) {
      let validityError = null;
      if (options.customErrorValidation) {
        validityError = options.customErrorValidation(
          newValue,
          options,
          context
        );
      }
      if (validityError) {
        noticeIsOutdated = true;
        const partialUpdate_withError = {
          [`${context.name}Error`]: validityError,
        } as Partial<Form>;
        context = context.mutate.withPartialForm(
          context,
          partialUpdate_withError
        );
      }
    }

    const itsDifferentFromWhatWouldBeShownWithoutChangingIt =
      newValue !== prevValue || newValue != currentValue;
    if (itsDifferentFromWhatWouldBeShownWithoutChangingIt) {
      noticeIsOutdated = true;
      const partialUpdate_withValueChange = {
        [context.name]: newValue,
      } as Partial<Form>;
      context.mutate.withPartialForm(context, partialUpdate_withValueChange);
    }

    if (noticeIsOutdated) {
      const notice =
        options.noticeGenerator?.(newValue, options, context) || {};
      const partialUpdate_withError = {
        ...notice,
      } as Partial<Form>;
      context = context.mutate.withPartialForm(
        context,
        partialUpdate_withError
      );
    }
  };

export type GenericFormLogic<
  ThisForm extends GenericForm,
  App extends unknown
> = {
  readonly [key in keyof ThisForm]?: GenericFormLogicalField<ThisForm, App>;
};

export interface UseFormInterface<Form extends GenericForm> {
  form: Form;
  onFormFieldFocus: (fieldName: string) => void;
  onFormInputChange: (name: string, value: any) => void;
  onFormInputBlur: (name: string) => void;
  validateRightNowAndGiveMeAYesOrNo: () => boolean;
  resetForm: () => void;
  inject: (partialFormUpdate: Partial<Form>) => void;
}

export type GenericFormConsumerInterface<Form extends GenericForm> = Omit<
  UseFormInterface<Form>,
  "resetForm" | "validateRightNowAndGiveMeAYesOrNo" | "inject"
>;

export const useForm = <Form extends GenericForm, App extends unknown>(
  FormLogic: GenericFormLogic<Form, App>,
  AppContext: () => App,
  initialState: () => Form
) => {
  const [form, setForm] = React.useState<Form>(initialState);
  const formReference = React.useRef<Form | null>(null);

  /**
   * This is the debouncing for the fields dispatchers.
   */

  const handleDebounceDispatch = React.useCallback(
    async (name: string) => {
      const validationEvent = ValidationEvent.onDebouncedPurgatory;
      const validationEventHistory = [validationEvent];

      if (!formReference.current) {
        return;
      }
      // No events which the form logic describes interest in have fired yet
      if (
        !formReference.current?.[
          `${name}${FormFieldExtensionKeys.DebounceDispatchTrigger}`
        ]
      ) {
        return;
      }

      const theLastUpdate =
        formReference.current?.[`${name}${FormFieldExtensionKeys.LastUpdate}`];
      const theLastDebounceTime =
        form[`${name}${FormFieldExtensionKeys.LastDebounceDispatch}`];
      if (theLastUpdate < theLastDebounceTime) {
        return;
      }

      const thisDebounceDispatch = FormLogic[name]?.handleDebounceDispatch;
      if (thisDebounceDispatch) {
        const preexistingForm_thisCanGetStaleBeCareful = formReference.current;

        const context: GenericFormValidationContext<Form, App> = {
          validationEvent,
          validationEventHistory,

          name,
          prevForm: {
            ...preexistingForm_thisCanGetStaleBeCareful,
          },
          form: {
            ...preexistingForm_thisCanGetStaleBeCareful,
          },
          formReference,
          app: AppContext(),
        };

        try {
          await thisDebounceDispatch(context);
        } catch (unknownError: unknown) {
          console.error(unknownError);
        }
      }

      const lastUpdate_postAsyncLogic =
        formReference.current?.[`${name}${FormFieldExtensionKeys.LastUpdate}`];
      const thisDebounceDate = Date.now();
      const isThisDebounceUpdateTheLatest =
        !lastUpdate_postAsyncLogic ||
        lastUpdate_postAsyncLogic < thisDebounceDate;

      let newFormAdditions: { [key: string]: any } | null = null;
      if (isThisDebounceUpdateTheLatest) {
        newFormAdditions = {
          [`${name}${FormFieldExtensionKeys.LastDebounceDispatch}`]:
            thisDebounceDate,
          [`${name}${FormFieldExtensionKeys.DebounceDispatchTrigger}`]: false,
        };
      }

      if (newFormAdditions) {
        const preexistingForm_thisCanGetStaleBeCareful = formReference.current;
        const newForm = {
          ...preexistingForm_thisCanGetStaleBeCareful,
          ...newFormAdditions,
        };
        formReference.current = newForm;
        setForm(newForm);
      }
    },
    [form, setForm, AppContext]
  );
  for (const [key, value] of Object.entries(FormLogic)) {
    if (!value?.handleDebounceDispatch) {
      continue;
    }

    const dependencies = [
      form[`${key}${FormFieldExtensionKeys.DebounceDispatchTrigger}`],
    ];

    React.useEffect(() => {
      if (!formReference.current) {
        return;
      }
      const timeout: number = window.setTimeout(() => {
        handleDebounceDispatch(key);
      }, value.debounceDelay || 500);
      return () => {
        clearTimeout(timeout);
      };
    }, dependencies);
  }

  /**
   * This is the debouncing for the old debounced validation style
   */

  const handleDebouncedValidation = React.useCallback(
    async (name: string) => {
      const validationEvent = ValidationEvent.onDebouncedPurgatory;
      const validationEventHistory = [validationEvent];

      // No events which the form logic describes interest in have fired yet
      if (!formReference.current?.[`${name}DebouncingTrigger`]) {
        return;
      }

      if (!formReference.current) {
        return;
      }

      const theLastUpdate = formReference.current?.[`${name}LastUpdate`];
      const theLastDebounceTime = form[`${name}LastDebounce`];
      if (theLastUpdate < theLastDebounceTime) {
        return;
      }

      let validationResult: GenericFormFieldWhichIsDebounced | undefined;
      const thisDebouncedValidation = FormLogic[name]?.debouncedValidate;
      if (thisDebouncedValidation) {
        const withParticularDebouncedValidationResult: (
          context: GenericFormValidationContext_forDebouncedValidation<
            Form,
            App,
            GenericFormFieldWhichIsDebounced
          >,
          debouncedField: GenericFormFieldWhichIsDebounced
        ) => GenericFormValidationContext_forDebouncedValidation<
          Form,
          App,
          GenericFormFieldWhichIsDebounced
        > = (context, debouncedField) => {
          validationResult = debouncedField;
          return context;
        };

        const preexistingForm_thisCanGetStaleBeCareful = formReference.current;
        const context: GenericFormValidationContext_forDebouncedValidation<
          Form,
          App,
          GenericFormFieldWhichIsDebounced
        > = {
          validationEvent,
          validationEventHistory,

          name,
          prevForm: {
            ...preexistingForm_thisCanGetStaleBeCareful,
          },
          form: {
            ...preexistingForm_thisCanGetStaleBeCareful,
          },
          formReference,
          app: AppContext(),

          mutate: {
            withParticularDebouncedValidationResult,
          },
        };

        try {
          await thisDebouncedValidation(context);
        } catch (unknownError: unknown) {
          console.error(unknownError);
        }
      }

      const lastUpdate_postAsyncLogic =
        formReference.current?.[`${name}LastUpdate`];
      const thisDebounceDate = Date.now();
      const isThisDebounceUpdateTheLatest =
        !lastUpdate_postAsyncLogic ||
        lastUpdate_postAsyncLogic < thisDebounceDate;

      let newFormAdditions: { [key: string]: any } | null = null;
      if (isThisDebounceUpdateTheLatest) {
        newFormAdditions = {
          [`${name}${FormFieldExtensionKeys.DebouncedValidation}`]:
            validationResult,
          [`${name}${FormFieldExtensionKeys.LastDebounce}`]: thisDebounceDate,
          [`${name}${FormFieldExtensionKeys.DebouncingTrigger}`]: false,
        };
      }

      if (newFormAdditions) {
        const preexistingForm_thisCanGetStaleBeCareful = formReference.current;
        const newForm = {
          ...preexistingForm_thisCanGetStaleBeCareful,
          ...newFormAdditions,
        };
        formReference.current = newForm;
        setForm(newForm);
      }
    },
    [form, setForm, AppContext]
  );
  for (const [key, value] of Object.entries(FormLogic)) {
    if (!value?.debouncedValidate) {
      continue;
    }

    const dependencies = [form];

    React.useEffect(() => {
      if (!formReference.current) {
        return;
      }
      const timeout: number = window.setTimeout(() => {
        handleDebouncedValidation(key);
      }, value.debounceDelay || 500);
      return () => {
        clearTimeout(timeout);
      };
    }, dependencies);
  }
  // React.useEffect(() => {
  //   let timeouts: number[] = [];
  //   if (formReference.current) {
  //     for (const [key, value] of Object.entries(FormLogic)) {
  //       if (!!value?.debouncedValidate || !!value?.handleDebounceDispatch) {
  //         timeouts.push(
  //           window.setTimeout(() => {
  //             handleDebouncedValidation(key);
  //           }, value.debounceDelay || 500)
  //         );
  //       }
  //     }
  //   }
  //   return () => {
  //     timeouts.forEach((timeout) => clearTimeout(timeout));
  //   };
  // }, [form]);

  /**
   * This adds support for React style effects in form fields
   * to act as the actual validation of any debounced result
   * seperate from the triggering logic.
   *
   * The previous all-in-one- debouncing style,
   * it overloads responsibilities and precludes you from
   * extending the React architecture.
   *
   * Now with this you can run logic in pure React terms.
   */

  interface AnyValidationEvent_GreaterValidationContext {
    name: string;
    value?: any;
    validationEvent: ValidationEvent;
    onAnyValidationMutation?: (
      context: GenericFormValidationContext<Form, App>
    ) => void;
    validationEventHistory?: ValidationEvent[];
  }
  const onAnyValidationEvent = React.useCallback(
    ({
      name,
      value,
      validationEvent,
      onAnyValidationMutation,
      validationEventHistory,
    }: AnyValidationEvent_GreaterValidationContext) => {
      validationEventHistory = [
        ...(validationEventHistory || []),
        ...[validationEvent],
      ];

      const doesThisEventCareAboutTheInputValue =
        ValidationEventsWithValues.some((rhs) => validationEvent === rhs);

      const doesThisEventCareAboutDebouncing =
        ValidationEventsWithDebouncing.some((rhs) => validationEvent === rhs);

      let preexistingForm = formReference.current || form;
      let validationResult: Partial<Form> = {};
      if (doesThisEventCareAboutTheInputValue) {
        validationResult = {
          ...validationResult,
          ...{
            [name]: value,
          },
        };
      }

      let yieldedEvents: GenericFormValidation_YieldEvent[] = [];
      let wasThereADeletionInTheFormState = false;
      const isThisAValidationEventThatThisFormLogicHandles =
        FormLogic[name]?.cleanEvents?.includes(validationEvent) ||
        FormLogic[name]?.validationEvents?.includes(validationEvent) ||
        validationEvent === ValidationEvent.onDependencyEvent ||
        validationEvent === ValidationEvent.handleEffect;
      if (isThisAValidationEventThatThisFormLogicHandles) {
        const genericContext: GenericFormValidationContext<Form, App> = {
          validationEventHistory,
          validationEvent,
          name,
          prevForm: {
            ...preexistingForm,
          },
          form: {
            ...preexistingForm,
            ...validationResult,
          },
          formReference,
          app: AppContext(),
        };

        /**
         * beforeValidate
         */
        const beforeValidate = FormLogic[name]?.beforeValidate;
        const deleteByKey = (
          context: GenericFormValidationContext_forBeforeValidate<Form, App>,
          keyToDelete: any
        ): GenericFormValidationContext_forBeforeValidate<Form, App> => {
          if (
            Object.keys(context.form).some(
              (aKeyInTheFormState) => aKeyInTheFormState === keyToDelete
            )
          ) {
            wasThereADeletionInTheFormState = true;

            const { [keyToDelete]: dead, ...formCleanedOfPreviousError } =
              context.form as { [key: string]: any };

            context.form = formCleanedOfPreviousError as Form;
          }
          return context;
        };
        const beforeValidateContext: GenericFormValidationContext_forBeforeValidate<
          Form,
          App
        > = {
          ...genericContext,
          mutate: {
            deleteByKey,
          },
        };
        const thisEventAsksForACleanAboutNow =
          validationEvent === ValidationEvent.onDependencyEvent ||
          (
            FormLogic[name]?.cleanEvents || FormLogic[name]?.validationEvents
          )?.some(
            (aCleaningEventThatThisFormLogicalFieldCaresAbout) =>
              aCleaningEventThatThisFormLogicalFieldCaresAbout ===
              validationEvent
          );
        if (thisEventAsksForACleanAboutNow && beforeValidate) {
          beforeValidate(beforeValidateContext);
        }
        preexistingForm = beforeValidateContext.form;

        /**
         * generic validation convenience
         */
        let runningForm = beforeValidateContext.form;
        const mutate_withPartialForm = (
          context: GenericFormValidationContext_forValidation<Form, App>,
          partialForm: Partial<Form>
        ): GenericFormValidationContext_forValidation<Form, App> => {
          validationResult = {
            ...validationResult,
            ...partialForm,
          };
          context.form = {
            ...context.form,
            ...partialForm,
          };
          onAnyValidationMutation?.(context);
          return context;
        };
        const yield_validationEvent: GenericFormValidationContext_forEffect<
          Form,
          App
        >["yield"]["validationEvent"] = (context, event) => {
          yieldedEvents = [...yieldedEvents, ...[event]];

          context.yielded.validationEvents = yieldedEvents;

          return context;
        };
        const produceGenericValidationContext = () => {
          const genericValidationContext: GenericFormValidationContext_forValidation<
            Form,
            App
          > = {
            ...genericContext,
            form: runningForm,
            mutate: {
              withPartialForm: mutate_withPartialForm,
            },
          };
          return genericValidationContext;
        };
        const produceGenericValidationContext_forEffect = () => {
          const genericValidationContext: GenericFormValidationContext_forEffect<
            Form,
            App
          > = {
            ...genericContext,
            form: runningForm,
            mutate: {
              withPartialForm: mutate_withPartialForm,
            },
            yield: {
              validationEvent: yield_validationEvent,
            },
            yielded: {
              validationEvents: yieldedEvents,
            },
          };
          return genericValidationContext;
        };

        /**
         * validate
         */
        const isThisAValidationEvent_thatRequiresValidation =
          FormLogic[name]?.validationEvents?.includes(validationEvent) ||
          validationEvent === ValidationEvent.onDependencyEvent;
        if (isThisAValidationEvent_thatRequiresValidation) {
          const validationContext = produceGenericValidationContext();
          FormLogic[name]?.validate(validationContext);
          runningForm = validationContext.form;
        }

        /**
         * handleEffect
         */
        if (validationEvent === ValidationEvent.handleEffect) {
          const validationContext = produceGenericValidationContext_forEffect();
          FormLogic[name]?.handleEffect?.(validationContext);
          runningForm = validationContext.form;
        }
      }

      let wereThereChanges =
        Object.keys(validationResult).length > 0 ||
        wasThereADeletionInTheFormState;

      if (
        doesThisEventCareAboutDebouncing &&
        isThisAValidationEventThatThisFormLogicHandles
      ) {
        validationResult = {
          ...validationResult,
          [`${name}${FormFieldExtensionKeys.DebouncingTrigger}`]: true,
          [`${name}${FormFieldExtensionKeys.DebounceDispatchTrigger}`]: true,
        };
        wereThereChanges = true;
      }

      if (wereThereChanges) {
        const thisUpdate = Date.now();
        const newForm = {
          ...preexistingForm,
          [`${name}${FormFieldExtensionKeys.LastUpdate}`]: thisUpdate,
          ...validationResult,
        };

        formReference.current = newForm;
        setForm(newForm);
      }

      let fieldsWhichDependOnThisField = [];
      for (const [key, value] of Object.entries(FormLogic)) {
        if (value?.dependencies?.includes(name)) {
          fieldsWhichDependOnThisField.push(key);
        }
      }

      fieldsWhichDependOnThisField.forEach((fieldName) => {
        onAnyValidationEvent({
          name: fieldName,
          validationEventHistory,
          validationEvent: ValidationEvent.onDependencyEvent,
          onAnyValidationMutation,
        });
      });

      yieldedEvents.forEach((event) => {
        onAnyValidationEvent({
          name: event.name,
          value: event.value,
          validationEventHistory,
          validationEvent: event.validationEvent,
          onAnyValidationMutation,
        });
      });
    },
    [form, setForm, AppContext]
  );
  for (const [key, value] of Object.entries(FormLogic)) {
    const thisEffect = value?.handleEffect;
    if (!thisEffect) {
      continue;
    }

    const context_produceEffectDependencies: GenericFormValidationContext_forProducingDebounceEffectDependencies<
      Form,
      App
    > = {
      name: key,
      prevForm: {
        ...form,
      },
      form: {
        ...form,
      },
      formReference,
      app: AppContext(),
    };

    const thisDebouncedFieldEffectDependencies = [
      ...[form[key]],
      ...(value?.produceEffectDependencies?.(
        context_produceEffectDependencies
      ) || []),
    ];
    React.useEffect(() => {
      onAnyValidationEvent({
        name: key,
        validationEvent: ValidationEvent.handleEffect,
      });
    }, thisDebouncedFieldEffectDependencies);
  }

  // ---

  const onFormFieldFocus = React.useCallback(
    async (fieldName: string) => {
      onAnyValidationEvent({
        name: fieldName,
        validationEvent: ValidationEvent.onFocus,
      });
    },
    [onAnyValidationEvent]
  );

  const onFormInputChange = React.useCallback(
    async (name: string, value: any) => {
      if (form[name] === value) {
        return;
      }
      onAnyValidationEvent({
        name,
        value,
        validationEvent: ValidationEvent.onChange,
      });
    },
    [form, onAnyValidationEvent]
  );

  const onFormInputBlur = React.useCallback(
    (name: string) => {
      onAnyValidationEvent({
        name,
        validationEvent: ValidationEvent.onBlur,
      });
    },
    [onAnyValidationEvent]
  );

  const validateRightNowAndGiveMeAYesOrNo = React.useCallback(() => {
    const formPreValidated = form;

    let didAnythingNeedToChange = false;
    const onAnyValidationMutation = () => {
      didAnythingNeedToChange = true;
    };
    for (const [key] of Object.entries(FormLogic)) {
      onAnyValidationEvent({
        name: key,
        validationEvent: ValidationEvent.onSubmit,
        onAnyValidationMutation,
      });
    }

    if (didAnythingNeedToChange) {
      return false;
    }

    // if any of the last debounces are before the last update, then it means that the form is invalid
    for (const [key, value] of Object.entries(FormLogic)) {
      if (value?.debouncedValidate) {
        const lastUpdate = formPreValidated[`${key}LastUpdate`];
        const lastDebounce = formPreValidated[`${key}LastDebounce`];

        const lastUpdateMustBeBeforeLastDebounce =
          !value.debouncedValidationResultCanBeStale;
        const isLastDebounceBeforeLastUpdate = lastDebounce < lastUpdate;
        if (
          lastUpdateMustBeBeforeLastDebounce &&
          isLastDebounceBeforeLastUpdate
        ) {
          return false;
        }
      }
    }

    // if any of the debouncers results are considered in an erronous state, then it means that the form is invalid
    for (const [key, value] of Object.entries(FormLogic)) {
      if (value?.debouncedValidate) {
        const debouncedValidation =
          formPreValidated[`${key}DebouncedValidation`];

        const isThisValidationResultValid_theFunction =
          value?.debouncedValidationResultIsValid;
        if (isThisValidationResultValid_theFunction) {
          const isThisDebouncedResultValid =
            isThisValidationResultValid_theFunction(debouncedValidation);
          if (!isThisDebouncedResultValid) {
            return false;
          }
        }
      }
    }

    return true;
  }, [form, onAnyValidationEvent]);

  const resetForm = React.useCallback(() => {
    formReference.current = null;
    setForm(initialState());
  }, [setForm, initialState]);

  const inject = React.useCallback(
    (partialFormUpdate: Partial<Form>) => {
      const preexistingForm = formReference.current || form;

      const newForm = {
        ...preexistingForm,
        ...partialFormUpdate,
      };

      formReference.current = newForm;
      setForm(newForm);
    },
    [form, setForm]
  );

  const value: UseFormInterface<Form> = React.useMemo(
    () => ({
      form,
      onFormFieldFocus,
      onFormInputChange,
      onFormInputBlur,
      validateRightNowAndGiveMeAYesOrNo,
      resetForm,
      inject,
    }),
    [
      form,
      onFormFieldFocus,
      onFormInputChange,
      onFormInputBlur,
      validateRightNowAndGiveMeAYesOrNo,
      resetForm,
      inject,
    ]
  );

  return value;
};

export const GenericReactProviderFormInterfaceOutput = <
  Form extends GenericForm,
  App extends unknown
>(
  form: UseFormInterface<Form>
): GenericFormConsumerInterface<Form> => {
  return {
    ...form,
  };
};
